/** Normaliza fechas del formulario a yyyy-mm-dd antes de enviar al API A3. */
export function normalizeFechaIso(raw) {
  if (!raw) return "";
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return raw.toISOString().slice(0, 10);
  }
  const s = String(raw).trim();
  if (!s) return "";
  const isoPrefix = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoPrefix) return isoPrefix[1];
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) {
    const p1 = Number(slash[1]);
    const p2 = Number(slash[2]);
    const year = slash[3];
    let day;
    let month;
    if (p1 > 12) {
      day = p1;
      month = p2;
    } else if (p2 > 12) {
      month = p1;
      day = p2;
    } else {
      day = p1;
      month = p2;
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) return "";
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return "";
}


export const normStr = (s) => String(s ?? "").trim();

export function packLinepediFromGridPure(art, famCol, famRow, colTalla, rowTalla) {
  const fc = normStr(famCol);
  const fr = normStr(famRow);
  const ah = normStr(art?.CODFAMTALLAH ?? art?.codfamtallah ?? "");
  const av = normStr(art?.CODFAMTALLAV ?? art?.codfamtallav ?? "");
  const colCod = normStr(colTalla?.CODTALLA);
  const rowCod = normStr(rowTalla?.CODTALLA);
  if (!fc || !fr || !colCod || !rowCod) return null;
  if (ah && av && fc === ah && fr === av) {
    return {
      codFamTallaH: famCol,
      codTallaH: colCod,
      codFamTallaV: famRow,
      codTallaV: rowCod,
    };
  }
  if (ah && av && fc === av && fr === ah) {
    return {
      codFamTallaH: famRow,
      codTallaH: rowCod,
      codFamTallaV: famCol,
      codTallaV: colCod,
    };
  }
  return {
    codFamTallaH: famCol,
    codTallaH: colCod,
    codFamTallaV: famRow,
    codTallaV: rowCod,
  };
}

export function lineasPackMatch(l, p) {
  return (
    p &&
    normStr(l.codFamTallaH) === normStr(p.codFamTallaH) &&
    normStr(l.codTallaH) === normStr(p.codTallaH) &&
    normStr(l.codFamTallaV) === normStr(p.codFamTallaV) &&
    normStr(l.codTallaV) === normStr(p.codTallaV)
  );
}

export function matrizToNumber(value) {
  const raw = String(value ?? "")
    .trim()
    .replace(",", ".");
  if (!raw) return 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function fmtQtyMatriz(value) {
  return Number(value || 0).toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function tallaLabel(t) {
  return normStr(t?.CODTALLA);
}

export function colorLabel(t) {
  const d = normStr(t?.DESCRIPCION);
  if (d) return d;
  const c = normStr(t?.CODTALLA);
  return c.replace(/^\d+\s*[-/]?\s*/u, "").trim() || c;
}

const familyScore = (f, kind) => {
  const txt = `${normStr(f?.CODFAMTALLA)} ${normStr(f?.DESCFAMTALLA)}`.toLowerCase();
  const tallaHints = /(talla|size|medida|numeraci[oó]n|num)/i;
  const colorHints = /(color|colores|colour|accesor|accessor|acc)/i;
  if (kind === "talla") return tallaHints.test(txt) ? 2 : colorHints.test(txt) ? 0 : 1;
  return colorHints.test(txt) ? 2 : tallaHints.test(txt) ? 0 : 1;
};

export function sortFamiliesByKind(list, kind) {
  return [...list].sort((a, b) => familyScore(b, kind) - familyScore(a, kind));
}

/** Línea envío guardada → forma interna matriz */
export function normLineaEnvio(l) {
  return {
    codFamTallaH: String(l.CodFamTallaH ?? l.codFamTallaH ?? "").trim(),
    codTallaH: String(l.CodTallaH ?? l.codTallaH ?? "").trim(),
    codTallaHDesc: String(l.CodTallaHDesc ?? l.codTallaHDesc ?? "").trim(),
    codFamTallaV: String(l.CodFamTallaV ?? l.codFamTallaV ?? "").trim(),
    codTallaV: String(l.CodTallaV ?? l.codTallaV ?? "").trim(),
    codTallaVDesc: String(l.CodTallaVDesc ?? l.codTallaVDesc ?? "").trim(),
    unidades: matrizToNumber(l.Unidades ?? l.unidades),
  };
}

/**
 * Ejes de matriz desde líneas guardadas (misma familia H/V en todas las líneas).
 * Si no, null → no se puede mostrar grilla homogénea.
 */
export function matrixViewFromSavedLineas(lineasRaw) {
  const src = Array.isArray(lineasRaw) ? lineasRaw : [];
  if (!src.length) return null;
  const normL = src.map(normLineaEnvio);
  const famH0 = normStr(normL[0].codFamTallaH);
  const famV0 = normStr(normL[0].codFamTallaV);
  if (!famH0 || !famV0) return null;
  const same = normL.every(
    (l) => normStr(l.codFamTallaH) === famH0 && normStr(l.codFamTallaV) === famV0,
  );
  if (!same) return null;

  const colOrder = [];
  const seenH = new Set();
  const rowOrder = [];
  const seenV = new Set();
  for (const l of normL) {
    const ch = normStr(l.codTallaH);
    const cv = normStr(l.codTallaV);
    if (ch && !seenH.has(ch)) {
      seenH.add(ch);
      colOrder.push(ch);
    }
    if (cv && !seenV.has(cv)) {
      seenV.add(cv);
      rowOrder.push(cv);
    }
  }
  if (!colOrder.length || !rowOrder.length) return null;

  const descHByCod = new Map();
  const descVByCod = new Map();
  for (const l of normL) {
    const ch = normStr(l.codTallaH);
    const cv = normStr(l.codTallaV);
    if (ch && !descHByCod.has(ch)) {
      const d = normStr(l.codTallaHDesc);
      descHByCod.set(ch, d);
    }
    if (cv && !descVByCod.has(cv)) {
      const d = normStr(l.codTallaVDesc);
      descVByCod.set(cv, d);
    }
  }
  const tallasH = colOrder.map((cod) => ({
    CODTALLA: cod,
    DESCRIPCION: descHByCod.get(cod) || "",
  }));
  const tallasV = rowOrder.map((cod) => ({
    CODTALLA: cod,
    DESCRIPCION: descVByCod.get(cod) || "",
  }));

  const qtyAt = (codV, codH) => {
    const r = normL.find(
      (x) => normStr(x.codTallaH) === normStr(codH) && normStr(x.codTallaV) === normStr(codV),
    );
    return r ? matrizToNumber(r.unidades) : 0;
  };

  const colTotals = tallasH.map((h) =>
    tallasV.reduce((s, v) => s + qtyAt(v.CODTALLA, h.CODTALLA), 0),
  );
  const total = normL.reduce((s, x) => s + matrizToNumber(x.unidades), 0);

  return { famH: famH0, famV: famV0, tallasH, tallasV, qtyAt, colTotals, total };
}

/**
 * Unidades de referencia de un pedido CRM vinculado: Units del presupuesto,
 * o si no hay estimate cargado, uds venta / compra del documento A3 en el vínculo.
 */
export function orderUnitsReferenceFromPedido(p) {
  if (!p) return 0;
  const uEst = matrizToNumber(p?.presupuesto?.Units);
  if (uEst > 0) return uEst;
  const uVen = matrizToNumber(p?.UnidadesVenta);
  if (uVen > 0) return uVen;
  return matrizToNumber(p?.UnidadesCompra);
}

/** Suma de referencia sobre todos los vínculos pedido–envío (misma lógica que orderUnitsReferenceFromPedido). */
export function sumOrderUnitsReferenceFromPedidoLinks(links) {
  let s = 0;
  for (const l of links || []) {
    s += orderUnitsReferenceFromPedido(l?.pedidoA3);
  }
  return s;
}
